// ─────────────────────────────────────────────────────────────────────────────
// ForecastChart – Pure SVG inline chart rendered when forecast_series tool
// runs. Replaces the ASCII art Claude used to scribble in code blocks.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from "react";

export interface ForecastChartData {
	historical: Array<{ x: string | number; y: number }>;
	predicted: Array<{
		x: string | number;
		y: number;
		lower: number;
		upper: number;
	}>;
	widget_title?: string;
	series_name?: string;
	method?: string;
	trend?: "increasing" | "decreasing" | "stable";
	target?: number;
}

interface ForecastChartProps {
	data: ForecastChartData;
}

const W = 380;
const H = 200;
const PAD = { top: 18, right: 18, bottom: 28, left: 38 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

export default function ForecastChart({ data }: ForecastChartProps) {
	const [hover, setHover] = useState<null | { kind: "h" | "p"; idx: number }>(null);

	const allPoints = useMemo(() => {
		const hist = data.historical.map((p, i) => ({
			...p,
			idx: i,
			isHist: true,
		}));
		const pred = data.predicted.map((p, i) => ({
			...p,
			idx: data.historical.length + i,
			isHist: false,
		}));
		return [...hist, ...pred];
	}, [data]);

	const yMin = useMemo(() => {
		let m = Infinity;
		for (const p of data.historical) m = Math.min(m, p.y);
		for (const p of data.predicted) m = Math.min(m, p.lower);
		if (data.target != null) m = Math.min(m, data.target);
		return m === Infinity ? 0 : m;
	}, [data]);

	const yMax = useMemo(() => {
		let m = -Infinity;
		for (const p of data.historical) m = Math.max(m, p.y);
		for (const p of data.predicted) m = Math.max(m, p.upper);
		if (data.target != null) m = Math.max(m, data.target);
		return m === -Infinity ? 1 : m;
	}, [data]);

	const yPad = (yMax - yMin) * 0.12 || 1;
	const yLo = yMin - yPad;
	const yHi = yMax + yPad;

	const xCount = allPoints.length;
	const xToPx = (idx: number) =>
		PAD.left + (xCount <= 1 ? PLOT_W / 2 : (idx / (xCount - 1)) * PLOT_W);
	const yToPx = (y: number) =>
		PAD.top + PLOT_H - ((y - yLo) / (yHi - yLo)) * PLOT_H;

	const histPath = data.historical
		.map(
			(p, i) =>
				`${i === 0 ? "M" : "L"} ${xToPx(i).toFixed(2)} ${yToPx(p.y).toFixed(2)}`
		)
		.join(" ");

	const predPath = data.predicted
		.map((p, i) => {
			const idx = data.historical.length + i;
			return `${i === 0 ? "M" : "L"} ${xToPx(idx).toFixed(2)} ${yToPx(p.y).toFixed(2)}`;
		})
		.join(" ");

	// Bridge: connect last historical point to first predicted point
	const bridge = (() => {
		if (data.historical.length === 0 || data.predicted.length === 0) return "";
		const lastH = data.historical[data.historical.length - 1];
		const firstP = data.predicted[0];
		const x1 = xToPx(data.historical.length - 1);
		const y1 = yToPx(lastH.y);
		const x2 = xToPx(data.historical.length);
		const y2 = yToPx(firstP.y);
		return `M ${x1.toFixed(2)} ${y1.toFixed(2)} L ${x2.toFixed(2)} ${y2.toFixed(2)}`;
	})();

	// Confidence band as a closed polygon
	const bandPath = (() => {
		if (data.predicted.length === 0) return "";
		const startIdx = data.historical.length;
		const upper = data.predicted
			.map(
				(p, i) =>
					`${i === 0 ? "M" : "L"} ${xToPx(startIdx + i).toFixed(2)} ${yToPx(p.upper).toFixed(2)}`
			)
			.join(" ");
		const lower = [...data.predicted]
			.reverse()
			.map(
				(p, i) =>
					`L ${xToPx(startIdx + (data.predicted.length - 1 - i)).toFixed(2)} ${yToPx(p.lower).toFixed(2)}`
			)
			.join(" ");
		return `${upper} ${lower} Z`;
	})();

	const yTicks = useMemo(() => {
		const ticks: number[] = [];
		const step = (yHi - yLo) / 4;
		for (let i = 0; i <= 4; i++) ticks.push(yLo + step * i);
		return ticks;
	}, [yLo, yHi]);

	const trendColor =
		data.trend === "decreasing"
			? "#DC2626"
			: data.trend === "increasing"
			? "#15803D"
			: "#6B7280";

	const xLabels = allPoints.map((p) => String(p.x));

	const formatNumber = (n: number) => {
		if (Math.abs(n) >= 10000) return n.toFixed(0);
		if (Math.abs(n) >= 100) return n.toFixed(1);
		return n.toFixed(2);
	};

	return (
		<div
			style={{
				margin: "10px 0",
				padding: "12px 14px",
				background:
					"linear-gradient(135deg, #FAF7FF 0%, #FFFFFF 60%, #FDF7FB 100%)",
				border: "1px solid #EAE3F4",
				borderRadius: 14,
				boxShadow: "0 1px 3px rgba(72, 42, 122, 0.05)",
			}}
		>
			{/* Header */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 10,
					marginBottom: 4,
					flexWrap: "wrap",
				}}
			>
				<div style={{ minWidth: 0, flex: 1 }}>
					<div
						style={{
							fontSize: 9.5,
							textTransform: "uppercase",
							letterSpacing: "0.06em",
							color: "#6B7280",
							fontWeight: 700,
						}}
					>
						Forecast{data.method ? ` · ${data.method}` : ""}
					</div>
					<div
						style={{
							fontSize: 12.5,
							fontWeight: 700,
							color: "#1E1730",
							marginTop: 1,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{data.widget_title ?? "Series"}
						{data.series_name ? ` · ${data.series_name}` : ""}
					</div>
				</div>
				{data.trend && (
					<span
						style={{
							fontSize: 10,
							fontWeight: 700,
							padding: "2px 8px",
							borderRadius: 999,
							background:
								data.trend === "decreasing"
									? "#FEE2E2"
									: data.trend === "increasing"
									? "#DCFCE7"
									: "#F3F4F6",
							color: trendColor,
							textTransform: "uppercase",
							letterSpacing: "0.04em",
						}}
					>
						{data.trend === "increasing"
							? "↑ up"
							: data.trend === "decreasing"
							? "↓ down"
							: "→ stable"}
					</span>
				)}
			</div>

			<svg
				viewBox={`0 0 ${W} ${H}`}
				width="100%"
				height="auto"
				style={{ display: "block", marginTop: 4, overflow: "visible" }}
				role="img"
				aria-label={`Forecast chart for ${data.widget_title ?? "series"}`}
			>
				<defs>
					<linearGradient id="cai-fc-band" x1="0" y1="0" x2="0" y2="1">
						<stop offset="0%" stopColor="#B31F7E" stopOpacity="0.20" />
						<stop offset="100%" stopColor="#482A7A" stopOpacity="0.06" />
					</linearGradient>
					<linearGradient id="cai-fc-line" x1="0" y1="0" x2="1" y2="0">
						<stop offset="0%" stopColor="#482A7A" />
						<stop offset="100%" stopColor="#B31F7E" />
					</linearGradient>
				</defs>

				{/* Y-axis grid + labels */}
				{yTicks.map((t, i) => (
					<g key={`y-${i}`}>
						<line
							x1={PAD.left}
							y1={yToPx(t)}
							x2={W - PAD.right}
							y2={yToPx(t)}
							stroke="#EEE7F6"
							strokeDasharray={i === 0 || i === yTicks.length - 1 ? "0" : "2 3"}
							strokeWidth={i === 0 ? 0.8 : 0.5}
						/>
						<text
							x={PAD.left - 5}
							y={yToPx(t) + 3}
							textAnchor="end"
							fontSize="9"
							fill="#9CA3AF"
							fontFamily="ui-sans-serif, system-ui"
						>
							{formatNumber(t)}
						</text>
					</g>
				))}

				{/* Target reference line */}
				{data.target != null && (
					<g>
						<line
							x1={PAD.left}
							y1={yToPx(data.target)}
							x2={W - PAD.right}
							y2={yToPx(data.target)}
							stroke="#15803D"
							strokeDasharray="4 3"
							strokeWidth={1}
							opacity="0.7"
						/>
						<text
							x={W - PAD.right - 4}
							y={yToPx(data.target) - 4}
							textAnchor="end"
							fontSize="9"
							fill="#15803D"
							fontWeight="700"
						>
							target {formatNumber(data.target)}
						</text>
					</g>
				)}

				{/* Forecast/historical divider */}
				{data.historical.length > 0 && data.predicted.length > 0 && (
					<line
						x1={xToPx(data.historical.length - 1)}
						y1={PAD.top}
						x2={xToPx(data.historical.length - 1)}
						y2={PAD.top + PLOT_H}
						stroke="#D4C8E6"
						strokeDasharray="3 3"
						strokeWidth={0.8}
					/>
				)}

				{/* Confidence band */}
				{bandPath && <path d={bandPath} fill="url(#cai-fc-band)" stroke="none" />}

				{/* Bridge line (last historical → first predicted), faint */}
				{bridge && (
					<path
						d={bridge}
						stroke="#B5A4D8"
						strokeWidth={1.2}
						fill="none"
						strokeDasharray="3 2"
					/>
				)}

				{/* Historical line */}
				{histPath && (
					<path
						d={histPath}
						stroke="url(#cai-fc-line)"
						strokeWidth={2}
						fill="none"
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}

				{/* Forecast line */}
				{predPath && (
					<path
						d={predPath}
						stroke="#B31F7E"
						strokeWidth={2}
						strokeDasharray="4 3"
						fill="none"
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}

				{/* Historical points */}
				{data.historical.map((p, i) => (
					<circle
						key={`h-${i}`}
						cx={xToPx(i)}
						cy={yToPx(p.y)}
						r={hover?.kind === "h" && hover.idx === i ? 4.5 : 3}
						fill="#fff"
						stroke="#482A7A"
						strokeWidth={1.6}
						onMouseEnter={() => setHover({ kind: "h", idx: i })}
						onMouseLeave={() => setHover(null)}
						style={{ cursor: "pointer", transition: "r 0.12s" }}
					/>
				))}

				{/* Forecast points */}
				{data.predicted.map((p, i) => {
					const idx = data.historical.length + i;
					return (
						<circle
							key={`p-${i}`}
							cx={xToPx(idx)}
							cy={yToPx(p.y)}
							r={hover?.kind === "p" && hover.idx === i ? 4.5 : 3}
							fill="#B31F7E"
							stroke="#fff"
							strokeWidth={1.4}
							onMouseEnter={() => setHover({ kind: "p", idx: i })}
							onMouseLeave={() => setHover(null)}
							style={{ cursor: "pointer", transition: "r 0.12s" }}
						/>
					);
				})}

				{/* X-axis labels (sparse: first, last, divider) */}
				{xLabels.length > 0 && (
					<g>
						<text
							x={xToPx(0)}
							y={H - PAD.bottom + 14}
							textAnchor="start"
							fontSize="9"
							fill="#6B7280"
						>
							{xLabels[0]}
						</text>
						{data.historical.length > 0 && data.predicted.length > 0 && (
							<text
								x={xToPx(data.historical.length - 1)}
								y={H - PAD.bottom + 14}
								textAnchor="middle"
								fontSize="9"
								fill="#6B7280"
								fontWeight="600"
							>
								now
							</text>
						)}
						<text
							x={xToPx(xLabels.length - 1)}
							y={H - PAD.bottom + 14}
							textAnchor="end"
							fontSize="9"
							fill="#B31F7E"
							fontWeight="600"
						>
							{xLabels[xLabels.length - 1]}
						</text>
					</g>
				)}

				{/* Tooltip */}
				{hover && (() => {
					const p =
						hover.kind === "h"
							? data.historical[hover.idx]
							: data.predicted[hover.idx];
					if (!p) return null;
					const idx =
						hover.kind === "h" ? hover.idx : data.historical.length + hover.idx;
					const cx = xToPx(idx);
					const cy = yToPx(p.y);
					const text =
						hover.kind === "p"
							? `${String(p.x)}: ${formatNumber(p.y)} (CI ${formatNumber((p as { lower: number }).lower)}–${formatNumber((p as { upper: number }).upper)})`
							: `${String(p.x)}: ${formatNumber(p.y)}`;
					const tipW = Math.min(220, text.length * 5.2 + 16);
					const tipX = Math.max(
						PAD.left,
						Math.min(W - PAD.right - tipW, cx - tipW / 2)
					);
					const tipY = cy - 26;
					return (
						<g pointerEvents="none">
							<rect
								x={tipX}
								y={tipY}
								width={tipW}
								height={20}
								rx={5}
								fill="#1E1730"
								opacity="0.94"
							/>
							<text
								x={tipX + tipW / 2}
								y={tipY + 13}
								textAnchor="middle"
								fontSize="10"
								fill="#fff"
								fontFamily="ui-sans-serif, system-ui"
							>
								{text}
							</text>
						</g>
					);
				})()}
			</svg>

			{/* Legend */}
			<div
				style={{
					display: "flex",
					gap: 12,
					fontSize: 10,
					color: "#6B7280",
					marginTop: 4,
					flexWrap: "wrap",
				}}
			>
				<LegendItem swatch={<LineSwatch gradient solid />} label="Historical" />
				<LegendItem swatch={<LineSwatch color="#B31F7E" dashed />} label="Forecast" />
				<LegendItem
					swatch={
						<span
							style={{
								display: "inline-block",
								width: 14,
								height: 8,
								background: "url(#cai-fc-band) #EAD7E5",
								borderRadius: 2,
								backgroundImage:
									"linear-gradient(180deg, rgba(179,31,126,0.20), rgba(72,42,122,0.06))",
							}}
						/>
					}
					label="95% CI"
				/>
				{data.target != null && (
					<LegendItem
						swatch={<LineSwatch color="#15803D" dashed />}
						label="Target"
					/>
				)}
			</div>
		</div>
	);
}

function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }) {
	return (
		<span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
			{swatch}
			{label}
		</span>
	);
}

function LineSwatch({
	color,
	gradient,
	solid,
	dashed,
}: {
	color?: string;
	gradient?: boolean;
	solid?: boolean;
	dashed?: boolean;
}) {
	return (
		<svg width="18" height="6" viewBox="0 0 18 6">
			<line
				x1="0"
				y1="3"
				x2="18"
				y2="3"
				stroke={gradient ? "url(#cai-fc-line-leg)" : color ?? "#482A7A"}
				strokeWidth="2"
				strokeDasharray={dashed ? "3 2" : undefined}
			/>
			{gradient && (
				<defs>
					<linearGradient id="cai-fc-line-leg" x1="0" y1="0" x2="1" y2="0">
						<stop offset="0%" stopColor="#482A7A" />
						<stop offset="100%" stopColor="#B31F7E" />
					</linearGradient>
				</defs>
			)}
		</svg>
	);
}
